import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Grid, IconButton, Modal, TextField, Typography, useTheme } from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatTemplate } from './ChatModal';

interface EditTemplateModalProps {
  open: boolean;
  onClose: () => void;
  template: ChatTemplate | null;
  onSave: (text: string) => void;
  loading: boolean;
}

export const EditTemplateModal = ({
  open,
  onClose,
  template,
  onSave,
  loading,
}: EditTemplateModalProps): ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [templateText, setTemplateText] = useState<string>('');

  useEffect(() => {
    if (template) {
      setTemplateText(template.text);
    } else {
      setTemplateText('');
    }
  }, [template]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 'sm',
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '4px',
    p: '8px 16px',
  };

  const handleSave = (): void => {
    onSave(templateText);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Grid container sx={modalStyle}>
        <Grid item sx={{ marginTop: '6px', width: '100%', position: 'relative' }}>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: -8,
              top: -8,
            }}
          >
            <Close />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: '600 !important', color: theme.palette.primary.main, marginBottom: '16px' }}
          >
            {template?.id ? t('chat.quickTexts.editTemplate') : t('chat.quickTexts.createTemplate')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={templateText}
            onChange={(e) => setTemplateText(e.target.value)}
            placeholder={t('chat.quickTexts.placeholder')}
            sx={{ marginBottom: '16px' }}
          />
        </Grid>
        <Grid item xs={12}>
          <LoadingButton
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 500,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
            variant="contained"
            fullWidth
            onClick={handleSave}
            loading={loading}
            disabled={loading || templateText.trim() === ''}
          >
            {t('chat.quickTexts.saveTemplate')}
          </LoadingButton>
        </Grid>
      </Grid>
    </Modal>
  );
};
